import { JSX } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { THEME_COLORS } from '@/constants/theme'

interface LeadGoalBarProps {
  // Option A: supply a pre-computed percent
  percent?: number
  // Option B: supply sourced & goal and let the component compute percent
  sourced?: number
  goal?: number
  trackClassName?: string
  fillClassName?: string
  animate?: boolean
  animationMs?: number
  ariaLabel?: string
  capAt100?: boolean
  mode?: 'card' | 'simple'
  glowDurationMs?: number // how long the gold glow/gradient effect persists (then becomes static gold)
  onReachGoal?: () => void
}

// Low-level reusable bar ONLY (no labels/counts). Different areas compose their own layout.
export function LeadGoalBar({
  percent,
  sourced,
  goal,
  trackClassName,
  fillClassName,
  animate = true,
  animationMs = 500,
  ariaLabel = 'Lead goal progress',
  capAt100 = true,
  mode = 'card',
  glowDurationMs = 4000,
  onReachGoal,
}: LeadGoalBarProps): JSX.Element {
  // Resolve percent from sourced/goal or provided percent
  let resolvedPct: number
  if (typeof sourced === 'number' && typeof goal === 'number' && goal > 0) {
    resolvedPct = (sourced / Math.max(1, goal)) * 100
  } else {
    resolvedPct = typeof percent === 'number' ? percent : 0
  }
  const willBeGoldInitial = resolvedPct >= 100
  if (capAt100) resolvedPct = Math.min(100, resolvedPct)
  const targetPct = Math.max(0, Math.round(resolvedPct))

  // Animation state (now applies to both modes when animate=true)
  const [displayPct, setDisplayPct] = useState<number>(animate ? 0 : targetPct)
  const [reachedGoal, setReachedGoal] = useState<boolean>(false)
  const [celebrating, setCelebrating] = useState<boolean>(false)

  useEffect(() => {
    // Reset for new target
    setReachedGoal(false)
    let endGlow: number | undefined
    if (!animate) {
      setDisplayPct(targetPct)
      if (targetPct >= 100) {
        setReachedGoal(true)
        setCelebrating(true)
        onReachGoal?.()
        endGlow = window.setTimeout(() => setCelebrating(false), glowDurationMs)
      }
      return () => { if (endGlow) clearTimeout(endGlow) }
    }
    // Animated path: start from 0 then transition to target
    setDisplayPct(0)
    const start = window.setTimeout(() => setDisplayPct(targetPct), 40)
    // Mark goal reached slightly after animation completes
    const mark = window.setTimeout(() => {
      if (targetPct >= 100) {
        setReachedGoal(true)
        setCelebrating(true)
        onReachGoal?.()
        endGlow = window.setTimeout(() => setCelebrating(false), glowDurationMs)
      }
    }, animationMs + 60)
    return () => {
      clearTimeout(start)
      clearTimeout(mark)
      if (endGlow) clearTimeout(endGlow)
    }
  }, [targetPct, animate, animationMs, glowDurationMs, onReachGoal])

  // Track styling
  let finalTrack = trackClassName
  if (!finalTrack) {
    finalTrack = 'w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden relative'
    if (reachedGoal && willBeGoldInitial) {
      finalTrack = 'w-full h-2 rounded-full overflow-hidden relative ' + (celebrating ? 'bg-yellow-100 dark:bg-yellow-900/40' : 'bg-amber-50 dark:bg-amber-900/30')
    }
  }

  // Fill styling
  const goldGradient = 'bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 animate-pulse'
  let fillClass: string | undefined
  let fillStyle: { backgroundColor?: string } = {}

  if (reachedGoal && willBeGoldInitial) {
    fillClass = celebrating ? goldGradient : 'bg-yellow-400'
  } else {
    // Use purple theme color for all non-gold states (unless custom fillClassName provided in simple mode)
    fillClass = mode === 'simple' ? fillClassName : undefined
    fillStyle = { backgroundColor: THEME_COLORS.PURPLE[500] }
  }

  const widthValue = displayPct
  const duration = animationMs

  return (
    <div
      className={finalTrack}
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={widthValue}
    >
      <div
        className={`${fillClass} h-full rounded-full ${animate ? 'transition-all' : ''}`}
        style={{ width: `${widthValue}%`, transitionDuration: animate ? `${duration}ms` : undefined, ...fillStyle }}
      />
      {reachedGoal && willBeGoldInitial && celebrating && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="w-full h-full opacity-30 bg-[radial-gradient(circle_at_50%_0%,white,rgba(255,255,255,0)_60%)]" />
        </div>
      )}
    </div>
  )
}
